import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { cn } from '@/shared/utils/cn';

type ToastType = 'info' | 'success' | 'error' | 'warning';

interface ToastOptions {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration: number;
}

interface CreateToastOptions {
  type?: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

type ToastContextValue = (options: CreateToastOptions) => void;

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION = 4000;

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

export const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toasts, setToasts] = useState<ToastOptions[]>([]);
  const timeoutsRef = useRef<Record<string, number>>({});

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timeoutId = timeoutsRef.current[id];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete timeoutsRef.current[id];
    }
  }, []);

  const push = useCallback(
    (options: CreateToastOptions) => {
      const id = generateId();
      const toast: ToastOptions = {
        id,
        type: options.type ?? 'info',
        title: options.title,
        description: options.description,
        duration: options.duration ?? DEFAULT_DURATION,
      };

      setToasts((prev) => [...prev, toast]);

      const timeoutId = window.setTimeout(() => {
        remove(id);
      }, toast.duration);

      timeoutsRef.current[id] = timeoutId;
    },
    [remove],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        className="pointer-events-none fixed right-6 top-6 z-[1100] grid gap-3 sm:right-8 sm:top-8"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto min-w-[240px] max-w-[320px] rounded-2xl border border-slate-400/25 bg-slate-900/90 p-4 text-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.04)] animate-in fade-in-0 slide-in-from-top-2',
              {
                info: 'border-sky-300/45 bg-sky-950/85 text-sky-50 shadow-[0_20px_45px_rgba(76,5,25,0.45),inset_0_1px_0_rgba(255,255,255,0.04)]',
                success:
                  'border-emerald-300/45 bg-emerald-950/85 text-emerald-50 shadow-[0_20px_45px_rgba(76,5,25,0.45),inset_0_1px_0_rgba(255,255,255,0.04)]',
                warning:
                  'border-yellow-300/45 bg-yellow-950/85 text-yellow-50 shadow-[0_20px_45px_rgba(76,5,25,0.45),inset_0_1px_0_rgba(255,255,255,0.04)]',
                error:
                  'border-rose-300/45 bg-red-950/85 text-rose-50 shadow-[0_20px_45px_rgba(76,5,25,0.45),inset_0_1px_0_rgba(255,255,255,0.04)]',
              }[toast.type],
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <strong className="text-sm font-semibold tracking-tight">
                {toast.title}
              </strong>
              <button
                type="button"
                className="ml-2 text-lg leading-none text-slate-200 transition-colors hover:text-sky-200 focus:outline-none"
                onClick={() => remove(toast.id)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {toast.description ? (
              <p className="mt-1 text-sm leading-relaxed text-slate-300/85">
                {toast.description}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
